import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import petService from '../../services/petService';
import BackButton from '../../components/BackButton';

const AnimalCategoryList = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    const result = await petService.adminFetchAnimalCategories();
    if (result.success) {
      setCategories(result.data.results || result.data);
    } else {
      message.error(result.error);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalVisible(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name_en: category.name_en,
      name_el: category.name_el,
      is_active: category.is_active
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    const result = await petService.adminDeleteAnimalCategory(id);
    if (result.success) {
      message.success(t('admin.animalCategories.deleteSuccess'));
      fetchCategories();
    } else {
      message.error(result.error);
    }
  };

  const handleExport = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.animalCategories.nameEn'), dataIndex: 'name_en' },
      { title: t('admin.animalCategories.nameEl'), dataIndex: 'name_el' },
      {
        title: t('common.status'),
        dataIndex: 'is_active',
        exportRender: (val) => val ? t('common.active') : t('common.inactive')
      }
    ];
    exportToExcel(categories, exportColumns, 'animal_categories', t('admin.animalCategories.title'));
    message.success(t('admin.animalCategories.exportSuccess'));
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    const result = editingCategory
      ? await petService.adminUpdateAnimalCategory(editingCategory.id, values)
      : await petService.adminCreateAnimalCategory(values);

    setSubmitting(false);

    if (result.success) {
      message.success(editingCategory ? t('admin.animalCategories.updateSuccess') : t('admin.animalCategories.createSuccess'));
      setModalVisible(false);
      form.resetFields();
      fetchCategories();
    } else {
      message.error(result.error);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60
    },
    {
      title: t('admin.animalCategories.nameEn'),
      dataIndex: 'name_en',
      key: 'name_en',
      sorter: (a, b) => a.name_en.localeCompare(b.name_en)
    },
    {
      title: t('admin.animalCategories.nameEl'),
      dataIndex: 'name_el',
      key: 'name_el'
    },
    {
      title: t('common.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
      filters: [
        { text: t('common.active'), value: true },
        { text: t('common.inactive'), value: false }
      ],
      onFilter: (value, record) => record.is_active === value
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('admin.animalCategories.deleteConfirm')}
            description={t('admin.animalCategories.deleteWarning')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            okType="danger"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />
      <Card
        title={t('admin.animalCategories.title')}
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!categories.length}
            >
              {t('admin.exportExcel')}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchCategories}
            >
              {t('common.refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('admin.animalCategories.addCategory')}
            </Button>
          </Space>
        }
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          {t('admin.animalCategories.description')}
        </p>
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => t('admin.animalCategories.totalCategories', { total })
          }}
        />
      </Card>

      <Modal
        title={editingCategory ? t('admin.animalCategories.editCategory') : t('admin.animalCategories.addCategory')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="name_en"
            label={t('admin.animalCategories.nameEn')}
            rules={[
              { required: true, message: t('admin.animalCategories.nameEnRequired') },
              { max: 100, message: t('admin.animalCategories.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('admin.animalCategories.nameEnPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="name_el"
            label={t('admin.animalCategories.nameEl')}
            rules={[
              { required: true, message: t('admin.animalCategories.nameElRequired') },
              { max: 100, message: t('admin.animalCategories.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('admin.animalCategories.nameElPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('common.status')}
            valuePropName="checked"
          >
            <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingCategory ? t('common.update') : t('common.create')}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AnimalCategoryList;
